import React, { useState, useEffect } from "react";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { VerticalTemplates } from "../../services/Templates";
import { useSiteContext } from "../../context/SiteContext";
import { usePubSub } from "@videosdk.live/react-sdk";
import useIsMobile from "../../hooks/useIsMobile";
import useIsTab from "../../hooks/useIsTab";
import logger from '../../utils/logger';

const CtaSection = ({ sideBarContainerWidth }) => {
  const {
    activeElement,
    setActiveElement,
    setShowElementSection,
    meetingRole,
  } = useSiteContext();
  const { sideBarMode, leftSideBarMode, setLeftSideBarMode, setSideBarMode } =
    useMeetingAppContext();
  const { publish: publishCloseElementSection } = usePubSub(
    "CLOSE_ELEMENT_SECTION"
  );
  const { publish: publishShowElementSection } = usePubSub(
    "SHOW_ELEMENT_SECTION"
  );
  const isMobile = useIsMobile();
  const isTab = useIsTab();

  const getPlacement = (position) => {
    logger("log", position);
    logger("log", sideBarMode);
    if (isMobile) {
      // On mobile, fill from top bar to bottom bar
      if (position === "left") {
        return "top-[64px] bottom-[60px] left-0";
      } else if (position === "right") {
        return "top-[64px] bottom-[60px] right-0";
      } else if (position === "center") {
        return "top-[64px] bottom-[60px] left-0 right-0 flex items-center justify-center px-4";
      } else if (position === "top") {
        return "top-[64px] left-0 right-0 flex items-start justify-center";
      } else if (position === "bottom") {
        return "bottom-[60px] left-0 right-0 flex items-end justify-center px-4 pb-2";
      }
      // Default to left if position not recognized
      return "top-[64px] bottom-[60px] left-0";
    }
    if (position === "left") {
      return "left-4";
    } else if (position === "right") {
      return "right-4";
    } else if (position === "center") {
      return "top-1/2 transform -translate-x-1/2 -translate-y-1/2";
    } else if (position === "top") {
      return "top-[var(--topbar-height,60px)]";
    } else if (position === "bottom") {
      return "bottom-0 ";
    }
  };

  const getRightPosition = (position) => {
    if (position === "right") {
      return `${sideBarContainerWidth + 16}px`;
    } else if (position === "bottom") {
      return `${sideBarContainerWidth + 16}px`;
    } else if (position === "top") {
      return `${sideBarContainerWidth + 16}px`;
    }
    return "";
  };
  let bannerPosition = "left";
  try {
    bannerPosition =
      JSON.parse(activeElement?.settings || "{}").placement || "left";
  } catch (e) {
    logger("error", "Error parsing CTA settings:", e);
  }

  // Close right sidebar when right-positioned CTA is activated
  useEffect(() => {
    if (
      activeElement?.type === "cta" &&
      bannerPosition === "right" &&
      !isMobile &&
      !isTab &&
      sideBarMode
    ) {
      // Close the right sidebar when right CTA is activated
      setSideBarMode(null);
    }
  }, [activeElement?.id, bannerPosition, isMobile, isTab, setSideBarMode]);

  const CtaComponent = VerticalTemplates[activeElement?.displayStyle]?.preview;
  const isRightPositioned = bannerPosition === "right";
  const isLeftPositioned = bannerPosition === "left";
  const shouldShowInRightSidebar = isRightPositioned && !isMobile && !isTab;
  const shouldShowInLeftSidebar = isLeftPositioned && !isMobile && !isTab;
  // Hide right CTA when right sidebar is open (chat, participants, etc.)
  // Explicitly check if sideBarMode is truthy to ensure CTA hides when sidebar opens
  const shouldHideForRightSidebar = shouldShowInRightSidebar && !!sideBarMode;
  // Hide left CTA when left sidebar is open (elements)
  const shouldHideForLeftSidebar = shouldShowInLeftSidebar && !!leftSideBarMode;

  // Build style object conditionally
  const getStyle = () => {
    if (isMobile) {
      const style = {};

      // Set width based on position. For center, keep container full width so flex can center the banner.
      if (bannerPosition === "left" || bannerPosition === "right") {
        style.width = "auto";
        style.maxWidth = "288px"; // w-72 equivalent
      } else if (bannerPosition === "center") {
        // Don't set width here — container stays full width; inner wrapper constrains the card
        style.width = "100%";
      } else {
        style.width = "100%";
      }

      return style;
    } else {
      // If CTA is right-positioned, position it in the right sidebar area
      if (shouldShowInRightSidebar) {
        return {
          right: "0",
          top: "64px", // Below top bar
          bottom: "60px", // Above bottom bar
          width: `${sideBarContainerWidth}px`,
          height: "calc(100vh - 124px)", // Full height minus top and bottom bars
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px",
        };
      }

      // If CTA is left-positioned, position it in the left sidebar area
      if (shouldShowInLeftSidebar) {
        return {
          left: "0",
          top: "64px", // Below top bar
          bottom: "60px", // Above bottom bar
          width: `${sideBarContainerWidth}px`,
          height: "calc(100vh - 124px)", // Full height minus top and bottom bars
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px",
        };
      }

      // Desktop styles for other positions
      const hasRightSidebar = sideBarMode && !isMobile && !isTab;
      const hasLeftSidebar = leftSideBarMode && !isMobile && !isTab;
      const totalSidebarWidth =
        (hasRightSidebar ? sideBarContainerWidth : 0) +
        (hasLeftSidebar ? sideBarContainerWidth : 0);

      // Calculate width for bottom/top CTAs
      const getBottomTopWidth = () => {
        if (["bottom", "top"].includes(bannerPosition)) {
          if (hasRightSidebar || hasLeftSidebar) {
            return `calc(100% - ${totalSidebarWidth}px)`;
          }
          return "100%";
        }
        return null;
      };

      // Calculate left position for bottom/top CTAs when left sidebar is active
      const getBottomTopLeft = () => {
        if (["bottom", "top"].includes(bannerPosition) && hasLeftSidebar) {
          return `${sideBarContainerWidth}px`;
        }
        return null;
      };

      const bottomTopWidth = getBottomTopWidth();
      const bottomTopLeft = getBottomTopLeft();

      // Special handling for center CTA - should be in middle section with auto width
      if (bannerPosition === "center") {
        const style = {
          width: "auto", // Auto width for responsive sizing
          maxWidth: "90%", // Prevent it from being too wide
          top: "50%",
          transform: "translateX(-50%) translateY(-50%)",
        };

        // Calculate left position to center in the middle section (available space between sidebars)
        if (hasLeftSidebar && hasRightSidebar) {
          // Both sidebars open: center in middle section
          // Center point = left sidebar width + (viewport width - total sidebar width) / 2
          style.left = `calc(${sideBarContainerWidth}px + (100vw - ${totalSidebarWidth}px) / 2)`;
        } else if (hasLeftSidebar) {
          // Only left sidebar open: center in remaining space
          style.left = `calc(${sideBarContainerWidth}px + (100vw - ${sideBarContainerWidth}px) / 2)`;
        } else if (hasRightSidebar) {
          // Only right sidebar open: center in remaining space
          style.left = `calc((100vw - ${sideBarContainerWidth}px) / 2)`;
        } else {
          // No sidebars: center in full viewport
          style.left = "50%";
        }

        return style;
      }

      return {
        right: hasRightSidebar ? getRightPosition(bannerPosition) : "",
        width:
          bottomTopWidth !== null
            ? bottomTopWidth
            : bannerPosition === "left" || bannerPosition === "right"
            ? "auto"
            : "100%",
        left: bottomTopLeft !== null ? bottomTopLeft : "",
        transform:
          bannerPosition === "center" && (hasLeftSidebar || hasRightSidebar)
            ? "translateX(-50%) translateY(-50%)"
            : "",
      };
    }
  };

  // Get transform class for center positioning
  const getTransformClass = () => {
    // Don't add transform class if CTA is in sidebar area
    if (shouldShowInRightSidebar || shouldShowInLeftSidebar) {
      return "";
    }
    // Mobile center no longer uses translate transforms; it's flex-centered.
    if (!isMobile && bannerPosition === "center") {
      return "transform";
    }
    return "";
  };

  // Don't render if it should be hidden for sidebar
  if (shouldHideForRightSidebar || shouldHideForLeftSidebar) {
    return null;
  }

  // Don't render if there's no active element
  if (!activeElement || !CtaComponent) {
    return null;
  }

  const isMobileCenter = isMobile && bannerPosition === "center";

  return (
    <div
      className={`fixed transition-all duration-500 ease-in-out ${
        shouldShowInRightSidebar || shouldShowInLeftSidebar ? "z-40" : "z-10"
      } ${
        shouldShowInRightSidebar || shouldShowInLeftSidebar
          ? ""
          : getTransformClass()
      } ${
        shouldShowInRightSidebar || shouldShowInLeftSidebar
          ? ""
          : getPlacement(bannerPosition)
      }`}
      style={getStyle()}
    >
      {CtaComponent && activeElement && (
        <>
          {isMobileCenter ? (
            <div className="w-full flex justify-center items-center">
              <div className="w-[85%] max-w-[400px]">
                <CtaComponent
                  {...JSON.parse(activeElement.settings)}
                  inmeeting={true}
                  position={bannerPosition}
                  center={true}
                  onClose={() => {
                    setActiveElement(null);
                    if (meetingRole === "moderator") {
                      publishShowElementSection("false", { persist: true });
                      publishCloseElementSection(JSON.stringify({ set: true }));
                      setLeftSideBarMode("ELEMENTS");
                    }
                  }}
                />
              </div>
            </div>
          ) : (
            <CtaComponent
              {...JSON.parse(activeElement.settings)}
              inmeeting={true}
              position={bannerPosition}
              center={bannerPosition === "center"}
              onClose={() => {
                setActiveElement(null);
                if (meetingRole === "moderator") {
                  publishShowElementSection("false", { persist: true });
                  publishCloseElementSection(JSON.stringify({ set: true }));
                  setLeftSideBarMode("ELEMENTS");
                }
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CtaSection;
